import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Flame, Printer, Clock, Users, ShieldAlert } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Quarterly Evacuation Drill history viewer.
 * Renders every drill for a contractor or client with a prominent
 * "Print DSPD Drill Details" button per row. Used in both the
 * Admin Audit Report page and the Client Profile Document Hub tab.
 *
 * Props:
 *   clientId   — when set, filters drills that include this client
 *   contractorId — when set, filters drills for this contractor
 *   compact    — shrink padding (for embedded card in client profile)
 */
export default function DrillHistory({ clientId, contractorId, compact = false }) {
  const [drills, setDrills] = useState(null); // null = loading

  useEffect(() => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (contractorId) params.set("contractor_id", contractorId);
    api.get(`/evacuation-drills?${params.toString()}`).then(({ data }) => setDrills(Array.isArray(data) ? data : []))
      .catch(() => setDrills([]));
  }, [clientId, contractorId]);

  const printPdf = (drillId) => {
    // Use anchor with Authorization header isn't possible; instead use credentialed fetch + blob download
    // BUT since we have HTTP-only cookie auth, a plain anchor with the URL DOES carry the cookie.
    const url = `${BACKEND_URL}/api/exports/drill-pdf/${drillId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fmtDuration = (s) => {
    if (s == null) return "—";
    const sec = Number(s);
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  };

  return (
    <Card className={compact ? "p-4 space-y-3" : "p-5 space-y-3"} data-testid="drill-history-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-red-600" />
          <p className="text-label">Quarterly Evacuation Drill Log</p>
        </div>
        {drills && drills.length > 0 && (
          <Badge variant="secondary" className="text-[10px] font-bold uppercase tracking-wide" data-testid="drill-history-count">
            {drills.length} drill{drills.length === 1 ? "" : "s"}
          </Badge>
        )}
      </div>
      {drills === null ? (
        <p className="text-sm text-muted-foreground text-center py-4">Loading drills…</p>
      ) : drills.length === 0 ? (
        <div className="text-center py-6 border-2 border-dashed border-border rounded-md" data-testid="drill-history-empty">
          <ShieldAlert className="w-7 h-7 mx-auto text-muted-foreground mb-1" />
          <p className="text-sm font-semibold">No drill logs recorded yet.</p>
          <p className="text-xs text-muted-foreground">Contractor needs to log a quarterly drill from their dashboard.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="drill-history-list">
          {drills.map((d) => (
            <div
              key={d.id}
              className="grid md:grid-cols-[auto_1fr_auto] gap-3 items-center p-3 rounded-md border border-border bg-white"
              data-testid={`drill-row-${d.id}`}
            >
              <div className="flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-md px-3 py-2 min-w-[88px]">
                <p className="text-[10px] uppercase tracking-wide text-red-700 font-bold">Q{d.quarter || "—"} {d.year || ""}</p>
                <p className="font-display text-sm font-bold text-red-900">{d.drill_date}{d.drill_time ? ` ${d.drill_time}` : ""}</p>
              </div>
              <div className="text-sm space-y-1 min-w-0">
                <p className="font-display font-semibold">
                  {(d.drill_type || "general").replace(/\b\w/g, (c) => c.toUpperCase())}
                  {d.drill_classification && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                      {d.drill_classification}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDuration(d.evacuation_time_seconds)}</span>
                  {d.participant_count != null && (
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {d.participant_count} participants</span>
                  )}
                  {d.escape_route && <span className="truncate max-w-[280px]">Route: {d.escape_route}</span>}
                </p>
                {d.effectiveness_problems && (
                  <p className="text-xs text-foreground truncate" title={d.effectiveness_problems}>
                    <span className="font-semibold">Effectiveness:</span> {d.effectiveness_problems}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => printPdf(d.id)}
                data-testid={`print-drill-pdf-${d.id}`}
                className="bg-red-600 hover:bg-red-700 text-white whitespace-nowrap"
              >
                <Printer className="w-4 h-4 mr-1.5" /> Print DSPD Drill Details
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
